import { useEffect, useRef, useState } from "react";
import Modal from "react-responsive-modal";
import Icon from "@mdi/react";
import { mdiDelete, mdiDrag, mdiPen, mdiPlus } from "@mdi/js";
import { resizeImage } from "easy-image-resizer";
import {
    Draggable,
    moveItems,
    useDraggable,
    useDraggableContext,
} from "react-tiny-dnd";
import { DraggableContextType } from "react-tiny-dnd/lib/types";

import { useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { api } from "../services/api";
import { Carousel, ID } from "../../types";
import { Remove } from ".";

export default function Carousel({ children }: { children: React.ReactNode }) {
    const user = useUser();

    const [images, error, isLoading, reload] = useSSRHook<Carousel[]>("/api/carousel");
    const [activeIndex, setActiveIndex] = useState(0);
    useEffect(() => {
        if (images) {
            const interval = setInterval(() => {
                setActiveIndex((i) => (i + 1) % images.length);
            }, 7000);
            return () => clearInterval(interval);
        }
    }, [images]);

    const [showEdit, setShowEdit] = useState(false);
    const [message, setMessage] = useState("");

    const inputRef = useRef<HTMLInputElement>(null);

    // drag list
    const onDrop = async (dragIndex: number, overIndex: number) => {
        if (!images) return;

        const nextImages = moveItems(images, dragIndex, overIndex).map((
            image,
            order,
        ) => ({
            ...image,
            order,
        }));

        for (const image of nextImages) {
            await api.updateCarousel(image);
        }
        setMessage("Pořadí obrázků upraveno");
        reload();
    };

    const context = useDraggableContext({ onDrop });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function handleAddImages(e: any) {
        const files = e.target.files as File[];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;

            setMessage("Přidávám obrázek...");
            const url = await resizeImage(file, {
                maxWidth: 1920,
                maxHeight: 760,
                type: "webp",
            });
            await api.addCarousel({
                _id: "",
                order: images?.length ?? 0,
                image: { type: "EASY_DB_FILE", url },
            });
        }

        reload();
        setMessage("Obrázeky byly přidány");
    }

    async function handleRemove(id: ID) {
        setMessage("Obrázek se maže...");
        await api.removeCarousel({ carouselId: id });
        reload();
        setMessage("Obrázek byl smazán");
    }

    return (
        <div className="carousel">
            {images?.map((image, i) => (
                <div
                    key={image._id}
                    className={`carousel-image ${i === activeIndex ? "active" : ""
                        }`}
                    style={{ backgroundImage: `url(${image.image.url})` }}
                />
            ))}
            <div className="background">
                <div className="content">
                    {user.isAdmin && (
                        <div className="admin">
                            <button onClick={() => setShowEdit(true)}>
                                <Icon path={mdiPen} size={1} />{" "}
                                Upravit promítání
                            </button>
                            <Modal
                                open={showEdit}
                                onClose={() => setShowEdit(false)}
                            >
                                <h3>Upravit promítání</h3>
                                {message && <p className="alert">{message}</p>}
                                <div className="list slim no-select">
                                    {images?.map((image, i) => (
                                        <DraggableItem
                                            key={image._id}
                                            context={context}
                                            image={image}
                                            index={i}
                                            onRemove={() =>
                                                handleRemove(image._id)}
                                        />
                                    ))}
                                </div>
                                <button
                                    style={{ width: "100%" }}
                                    onClick={() => inputRef.current?.click()}
                                >
                                    <Icon path={mdiPlus} size={1} />{" "}
                                    Přidat obrázky
                                </button>
                                <input
                                    style={{ display: "none" }}
                                    type="file"
                                    ref={inputRef}
                                    accept="image/jpeg,image/png,image/webp"
                                    multiple
                                    onChange={handleAddImages}
                                />
                            </Modal>
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
}

function DraggableItem(
    { context, index, image, onRemove }: {
        context: DraggableContextType;
        index: number;
        image: Carousel;
        onRemove: () => void;
    },
) {
    const { listeners, isDragging } = useDraggable(context, index);

    return (
        <Draggable
            context={context}
            index={index}
            dividerClassName="dnd-divider"
        // preview={
        //     <img
        //         className="carousel-edit-image"
        //         src={image.image.url}
        //     />
        // }
        >
            <div className="item" style={{ opacity: isDragging ? 0.4 : 1 }}>
                <div className="dnd-button" {...listeners}>
                    <Icon path={mdiDrag} size={2} />
                </div>
                <img
                    className="carousel-edit-image"
                    src={image.image.url}
                />
                <div className="grow" />
                <Remove onRemove={onRemove} />
            </div>
        </Draggable>
    );
}
